import { Route } from '@angular/router';
import type {
  UiBreadcrumbItem,
  UiPageChromeConfig,
  UiTitleBarMode,
} from '@system-dyg/shared/ui/shell';
import { erpModuleEntry } from './data/erp-menubar.data';

const loadErpShell = () =>
  import('./shell/erp-shell.component').then(m => m.ErpShellComponent);

const loadModulePanel = () =>
  import('./pages/module-panel/erp-module-panel.component').then(
    m => m.ErpModulePanelComponent,
  );

const ERP_CRUMB: UiBreadcrumbItem = { id: 'erp', label: 'ERP' };

/** Módulo del ERP: raíz del breadcrumb de sus páginas y llave del menubar. */
type ErpModule = {
  id: string;
  label: string;
};

const TREASURY: ErpModule = { id: 'treasury', label: 'Tesorería' };
const PURCHASES_EXPENSES: ErpModule = {
  id: 'purchases-expenses',
  label: 'Compras y gastos',
};
const WAREHOUSES: ErpModule = { id: 'warehouses', label: 'Almacenes' };
const CONSTRUCTION: ErpModule = { id: 'construction', label: 'Control de obra' };
const SUPPLIERS: ErpModule = { id: 'suppliers', label: 'Proveedores' };
const SALES: ErpModule = { id: 'sales', label: 'Ventas' };
const CLIENTS: ErpModule = { id: 'clients', label: 'Clientes' };
const TITULATION: ErpModule = { id: 'titulation', label: 'Titulación' };
const HR: ErpModule = { id: 'human-resources', label: 'Recursos humanos' };
const FIXED_ASSETS: ErpModule = { id: 'fixed-assets', label: 'Activo fijo' };
const ACCOUNTING: ErpModule = { id: 'accounting', label: 'Contabilidad' };
const AUDIT: ErpModule = { id: 'audit', label: 'Auditoría' };

function moduleCrumbs(
  module: ErpModule,
  ...rest: readonly UiBreadcrumbItem[]
): readonly UiBreadcrumbItem[] {
  return [ERP_CRUMB, { id: module.id, label: module.label }, ...rest];
}

/** Marco de una página del ERP. */
function pageChrome(config: {
  module: ErpModule;
  activeMenubarId: string;
  title: string;
  titleBarMode: UiTitleBarMode;
  crumbs: readonly UiBreadcrumbItem[];
}): UiPageChromeConfig {
  return {
    moduleId: config.module.id,
    activeMenubarId: config.activeMenubarId,
    title: config.title,
    titleBarMode: config.titleBarMode,
    breadcrumbItems: config.crumbs,
  };
}

export const appRoutes: Route[] = [
  // ── Redirecciones ─────────────────────────────────────────────────────────
  // Van antes del shell: resuelven la URL sin montar ninguna página.
  { path: '', pathMatch: 'full', redirectTo: erpModuleEntry('treasury') },
  { path: 'treasury', pathMatch: 'full', redirectTo: erpModuleEntry('treasury') },
  { path: 'purchases-expenses', pathMatch: 'full', redirectTo: erpModuleEntry('purchases-expenses') },
  { path: 'warehouses', pathMatch: 'full', redirectTo: erpModuleEntry('warehouses') },
  { path: 'construction', pathMatch: 'full', redirectTo: erpModuleEntry('construction') },
  { path: 'suppliers', pathMatch: 'full', redirectTo: erpModuleEntry('suppliers') },
  { path: 'sales', pathMatch: 'full', redirectTo: erpModuleEntry('sales') },
  { path: 'clients', pathMatch: 'full', redirectTo: erpModuleEntry('clients') },
  { path: 'titulation', pathMatch: 'full', redirectTo: erpModuleEntry('titulation') },
  { path: 'human-resources', pathMatch: 'full', redirectTo: erpModuleEntry('human-resources') },
  { path: 'hr', pathMatch: 'full', redirectTo: erpModuleEntry('human-resources') },
  { path: 'hr/dashboard', pathMatch: 'full', redirectTo: erpModuleEntry('human-resources') },
  { path: 'hr/employees', pathMatch: 'full', redirectTo: 'human-resources/employees/employees' },
  { path: 'hr/payroll', pathMatch: 'full', redirectTo: 'human-resources/payroll/payroll' },
  { path: 'fixed-assets', pathMatch: 'full', redirectTo: erpModuleEntry('fixed-assets') },
  { path: 'accounting', pathMatch: 'full', redirectTo: erpModuleEntry('accounting') },
  { path: 'audit', pathMatch: 'full', redirectTo: erpModuleEntry('audit') },
  {
    // App Shell: única instancia del layout. Las páginas solo aportan contenido
    // y declaran su marco en `data.chrome`.
    path: '',
    loadComponent: loadErpShell,
    children: [
      // ── Tesorería ───────────────────────────────────────────────────────
      {
        path: 'treasury/income/income',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/treasury/income/income/income.component').then(
            m => m.IncomeComponent,
          ),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-income',
            title: 'Ingresos',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'income', label: 'Ingresos' },
              { id: 'income-list', label: 'Ingresos' },
            ),
          }),
        },
      },
      {
        path: 'treasury/expenses/expenses',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/treasury/expenses/expenses/expenses.component').then(
            m => m.TreasuryExpensesComponent,
          ),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-expenses',
            title: 'Egresos',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'expenses', label: 'Egresos' },
              { id: 'expenses-list', label: 'Egresos' },
            ),
          }),
        },
      },
      {
        path: 'treasury/expenses/expenses/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/treasury/expenses/expenses/expense-form/expense-form.component'
          ).then(m => m.TreasuryExpenseFormComponent),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-expenses',
            title: 'Nuevo egreso',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'expenses', label: 'Egresos' },
              { id: 'expenses-list', label: 'Egresos' },
              { id: 'new', label: 'Nuevo egreso' },
            ),
          }),
        },
      },
      {
        path: 'treasury/transfers/transfers',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/treasury/transfers/transfers/transfers.component').then(
            m => m.TransfersComponent,
          ),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-transfers',
            title: 'Traspasos',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'transfers', label: 'Traspasos' },
              { id: 'transfers-list', label: 'Traspasos' },
            ),
          }),
        },
      },
      {
        path: 'treasury/debtors/debtors',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/treasury/debtors/debtors/debtors.component').then(
            m => m.DebtorsComponent,
          ),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-debtors',
            title: 'Deudores',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'debtors', label: 'Deudores' },
              { id: 'debtors-list', label: 'Deudores' },
            ),
          }),
        },
      },
      {
        path: 'treasury/debtors/debtors/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/treasury/debtors/debtors/debtor-form/debtor-form.component'
          ).then(m => m.DebtorFormComponent),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-debtors',
            title: 'Nuevo deudor',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'debtors', label: 'Deudores' },
              { id: 'debtors-list', label: 'Deudores' },
              { id: 'new', label: 'Nuevo deudor' },
            ),
          }),
        },
      },
      {
        path: 'treasury/debtors/debtors/:debtorId/edit',
        loadComponent: () =>
          import(
            './pages/treasury/debtors/debtors/debtor-edit/debtor-edit.component'
          ).then(m => m.DebtorEditComponent),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-debtors',
            title: 'Editar deudor',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'debtors', label: 'Deudores' },
              { id: 'debtors-list', label: 'Deudores' },
              { id: 'edit', label: 'Editar deudor' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/treasury/debtors/debtors/debtor-edit/general/debtor-general.component'
              ).then(m => m.DebtorGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/treasury/debtors/debtors/debtor-edit/files/debtor-files.component'
              ).then(m => m.DebtorFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/treasury/debtors/debtors/debtor-edit/action-log/debtor-action-log.component'
              ).then(m => m.DebtorActionLogComponent),
          },
        ],
      },
      {
        path: 'treasury/creditors/creditors',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/treasury/creditors/creditors/creditors.component').then(
            m => m.CreditorsComponent,
          ),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-creditors',
            title: 'Acreedores',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'creditors', label: 'Acreedores' },
              { id: 'creditors-list', label: 'Acreedores' },
            ),
          }),
        },
      },
      {
        path: 'treasury/creditors/creditors/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/treasury/creditors/creditors/creditor-form/creditor-form.component'
          ).then(m => m.CreditorFormComponent),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-creditors',
            title: 'Nuevo acreedor',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'creditors', label: 'Acreedores' },
              { id: 'creditors-list', label: 'Acreedores' },
              { id: 'new', label: 'Nuevo acreedor' },
            ),
          }),
        },
      },
      {
        path: 'treasury/creditors/creditors/:creditorId/edit',
        loadComponent: () =>
          import(
            './pages/treasury/creditors/creditors/creditor-edit/creditor-edit.component'
          ).then(m => m.CreditorEditComponent),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-creditors',
            title: 'Editar acreedor',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'creditors', label: 'Acreedores' },
              { id: 'creditors-list', label: 'Acreedores' },
              { id: 'edit', label: 'Editar acreedor' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/treasury/creditors/creditors/creditor-edit/general/creditor-general.component'
              ).then(m => m.CreditorGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/treasury/creditors/creditors/creditor-edit/files/creditor-files.component'
              ).then(m => m.CreditorFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/treasury/creditors/creditors/creditor-edit/action-log/creditor-action-log.component'
              ).then(m => m.CreditorActionLogComponent),
          },
        ],
      },
      {
        path: 'treasury/investments/investments',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/treasury/investments/investments/investments.component').then(
            m => m.InvestmentsComponent,
          ),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-investments',
            title: 'Inversiones',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'investments', label: 'Inversiones' },
              { id: 'investments-list', label: 'Inversiones' },
            ),
          }),
        },
      },
      {
        path: 'treasury/credits',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/treasury/credits/credits.component').then(
            m => m.CreditsComponent,
          ),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-credits',
            title: 'Créditos',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(TREASURY, { id: 'credits', label: 'Créditos' }),
          }),
        },
      },
      {
        path: 'treasury/bank-accounts/bank-accounts',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/treasury/bank-accounts/bank-accounts/bank-accounts.component'
          ).then(m => m.BankAccountsComponent),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-cat-bank-accounts',
            title: 'Cuentas bancarias',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'bank-accounts', label: 'Cuentas bancarias' },
              { id: 'bank-accounts-list', label: 'Cuentas bancarias' },
            ),
          }),
        },
      },
      {
        path: 'treasury/bank-accounts/bank-accounts/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/treasury/bank-accounts/bank-accounts/bank-account-form/bank-account-form.component'
          ).then(m => m.BankAccountFormComponent),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-cat-bank-accounts',
            title: 'Nueva cuenta bancaria',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'bank-accounts', label: 'Cuentas bancarias' },
              { id: 'bank-accounts-list', label: 'Cuentas bancarias' },
              { id: 'new', label: 'Nueva cuenta bancaria' },
            ),
          }),
        },
      },
      {
        path: 'treasury/bank-accounts/bank-accounts/:bankAccountId/edit',
        loadComponent: () =>
          import(
            './pages/treasury/bank-accounts/bank-accounts/bank-account-edit/bank-account-edit.component'
          ).then(m => m.BankAccountEditComponent),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-cat-bank-accounts',
            title: 'Editar cuenta bancaria',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'bank-accounts', label: 'Cuentas bancarias' },
              { id: 'bank-accounts-list', label: 'Cuentas bancarias' },
              { id: 'edit', label: 'Editar cuenta bancaria' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/treasury/bank-accounts/bank-accounts/bank-account-edit/general/bank-account-general.component'
              ).then(m => m.BankAccountGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/treasury/bank-accounts/bank-accounts/bank-account-edit/files/bank-account-files.component'
              ).then(m => m.BankAccountFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/treasury/bank-accounts/bank-accounts/bank-account-edit/action-log/bank-account-action-log.component'
              ).then(m => m.BankAccountActionLogComponent),
          },
        ],
      },
      {
        path: 'treasury/cash-boxes/cash-boxes',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/treasury/cash-boxes/cash-boxes/cash-boxes.component').then(
            m => m.CashBoxesComponent,
          ),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-cat-cash-boxes',
            title: 'Cajas efectivo',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'cash-boxes', label: 'Cajas efectivo' },
              { id: 'cash-boxes-list', label: 'Cajas efectivo' },
            ),
          }),
        },
      },
      {
        path: 'treasury/cash-boxes/cash-boxes/nuevo',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/treasury/cash-boxes/cash-boxes/cash-box-form/cash-box-form.component'
          ).then(m => m.CashBoxFormComponent),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-cat-cash-boxes',
            title: 'Nueva caja efectivo',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'cash-boxes', label: 'Cajas efectivo' },
              { id: 'cash-boxes-list', label: 'Cajas efectivo' },
              { id: 'new', label: 'Nueva caja efectivo' },
            ),
          }),
        },
      },
      {
        path: 'treasury/cash-boxes/cash-boxes/:cashBoxId/edit',
        loadComponent: () =>
          import(
            './pages/treasury/cash-boxes/cash-boxes/cash-box-edit/cash-box-edit.component'
          ).then(m => m.CashBoxEditComponent),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-cat-cash-boxes',
            title: 'Editar caja efectivo',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'cash-boxes', label: 'Cajas efectivo' },
              { id: 'cash-boxes-list', label: 'Cajas efectivo' },
              { id: 'edit', label: 'Editar caja efectivo' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/treasury/cash-boxes/cash-boxes/cash-box-edit/general/cash-box-general.component'
              ).then(m => m.CashBoxGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/treasury/cash-boxes/cash-boxes/cash-box-edit/files/cash-box-files.component'
              ).then(m => m.CashBoxFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/treasury/cash-boxes/cash-boxes/cash-box-edit/action-log/cash-box-action-log.component'
              ).then(m => m.CashBoxActionLogComponent),
          },
        ],
      },
      {
        path: 'treasury/catalogs/bank-institutions',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/treasury/catalogs/bank-institutions/bank-institutions.component'
          ).then(m => m.BankInstitutionsComponent),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-cat-banks',
            title: 'Instituciones bancarias',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'tre-catalogs', label: 'Catálogos' },
              { id: 'bank-institutions', label: 'Instituciones bancarias' },
            ),
          }),
        },
      },
      {
        path: 'treasury/catalogs/bank-institutions/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/treasury/catalogs/bank-institutions/bank-institution-form/bank-institution-form.component'
          ).then(m => m.BankInstitutionFormComponent),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-cat-banks',
            title: 'Nueva institución bancaria',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'tre-catalogs', label: 'Catálogos' },
              { id: 'bank-institutions', label: 'Instituciones bancarias' },
              { id: 'new', label: 'Nueva institución bancaria' },
            ),
          }),
        },
      },
      {
        path: 'treasury/catalogs/bank-institutions/:bankInstitutionId/edit',
        loadComponent: () =>
          import(
            './pages/treasury/catalogs/bank-institutions/bank-institution-edit/bank-institution-edit.component'
          ).then(m => m.BankInstitutionEditComponent),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-cat-banks',
            title: 'Editar institución bancaria',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'tre-catalogs', label: 'Catálogos' },
              { id: 'bank-institutions', label: 'Instituciones bancarias' },
              { id: 'edit', label: 'Editar institución bancaria' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/treasury/catalogs/bank-institutions/bank-institution-edit/general/bank-institution-general.component'
              ).then(m => m.BankInstitutionGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/treasury/catalogs/bank-institutions/bank-institution-edit/files/bank-institution-files.component'
              ).then(m => m.BankInstitutionFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/treasury/catalogs/bank-institutions/bank-institution-edit/action-log/bank-institution-action-log.component'
              ).then(m => m.BankInstitutionActionLogComponent),
          },
        ],
      },
      {
        path: 'treasury/reports/cash-flow',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/treasury/reports/cash-flow/cash-flow.component').then(
            m => m.TreasuryReportCashFlowComponent,
          ),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-rep-cashflow',
            title: 'Flujo de efectivo',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'reports', label: 'Reportes' },
              { id: 'cash-flow', label: 'Flujo de efectivo' },
            ),
          }),
        },
      },
      {
        path: 'treasury/reports/banks',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/treasury/reports/banks/banks.component').then(
            m => m.TreasuryReportBanksComponent,
          ),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-rep-banks',
            title: 'Bancos',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'reports', label: 'Reportes' },
              { id: 'banks', label: 'Bancos' },
            ),
          }),
        },
      },
      {
        path: 'treasury/reports/cash-boxes',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/treasury/reports/cash-boxes/cash-boxes.component').then(
            m => m.TreasuryReportCashBoxesComponent,
          ),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-rep-cash-boxes',
            title: 'Cajas efectivo',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'reports', label: 'Reportes' },
              { id: 'cash-boxes', label: 'Cajas efectivo' },
            ),
          }),
        },
      },
      {
        path: 'treasury/reports/debtors',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/treasury/reports/debtors/debtors.component').then(
            m => m.TreasuryReportDebtorsComponent,
          ),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-rep-debtors',
            title: 'Deudores',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'reports', label: 'Reportes' },
              { id: 'debtors', label: 'Deudores' },
            ),
          }),
        },
      },
      {
        path: 'treasury/reports/creditors',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/treasury/reports/creditors/creditors.component').then(
            m => m.TreasuryReportCreditorsComponent,
          ),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-rep-creditors',
            title: 'Acreedores',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'reports', label: 'Reportes' },
              { id: 'creditors', label: 'Acreedores' },
            ),
          }),
        },
      },
      {
        path: 'treasury/reports/investments',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/treasury/reports/investments/investments.component').then(
            m => m.TreasuryReportInvestmentsComponent,
          ),
        data: {
          chrome: pageChrome({
            module: TREASURY,
            activeMenubarId: 'tre-rep-investments',
            title: 'Inversiones',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              TREASURY,
              { id: 'reports', label: 'Reportes' },
              { id: 'investments', label: 'Inversiones' },
            ),
          }),
        },
      },

      // ── Compras y gastos ────────────────────────────────────────────────
      {
        path: 'purchases-expenses/purchase-orders/purchase-orders',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/purchases-expenses/purchase-orders/purchase-orders/purchase-orders.component'
          ).then(m => m.PurchaseOrdersComponent),
        data: {
          chrome: pageChrome({
            module: PURCHASES_EXPENSES,
            activeMenubarId: 'pxe-ord-purchase-orders',
            title: 'Orden de compra',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              PURCHASES_EXPENSES,
              { id: 'orders', label: 'Orden de compra' },
              { id: 'purchase-orders', label: 'Orden de compra' },
            ),
          }),
        },
      },
      {
        path: 'purchases-expenses/expenses/expenses',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/purchases-expenses/expenses/expenses/expenses.component'
          ).then(m => m.ExpensesComponent),
        data: {
          chrome: pageChrome({
            module: PURCHASES_EXPENSES,
            activeMenubarId: 'pxe-exp-expenses',
            title: 'Gastos',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              PURCHASES_EXPENSES,
              { id: 'expenses', label: 'Gastos' },
              { id: 'expenses-list', label: 'Gastos' },
            ),
          }),
        },
      },
      {
        path: 'purchases-expenses/advances/advances',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/purchases-expenses/advances/advances/advances.component'
          ).then(m => m.AdvancesComponent),
        data: {
          chrome: pageChrome({
            module: PURCHASES_EXPENSES,
            activeMenubarId: 'pxe-adv-advances',
            title: 'Anticipos',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              PURCHASES_EXPENSES,
              { id: 'advances', label: 'Anticipos' },
              { id: 'advances-list', label: 'Anticipos' },
            ),
          }),
        },
      },
      {
        path: 'purchases-expenses/expenses/expenses/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/purchases-expenses/expenses/expenses/expense-form/expense-form.component'
          ).then(m => m.ExpenseFormComponent),
        data: {
          chrome: pageChrome({
            module: PURCHASES_EXPENSES,
            activeMenubarId: 'pxe-exp-expenses',
            title: 'Nuevo gasto',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              PURCHASES_EXPENSES,
              { id: 'expenses', label: 'Gastos' },
              { id: 'expenses-list', label: 'Gastos' },
              { id: 'new', label: 'Nuevo gasto' },
            ),
          }),
        },
      },
      {
        path: 'purchases-expenses/expenses/expenses/:expenseId/edit',
        loadComponent: () =>
          import(
            './pages/purchases-expenses/expenses/expenses/expense-edit/expense-edit.component'
          ).then(m => m.ExpenseEditComponent),
        data: {
          chrome: pageChrome({
            module: PURCHASES_EXPENSES,
            activeMenubarId: 'pxe-exp-expenses',
            title: 'Editar gasto',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              PURCHASES_EXPENSES,
              { id: 'expenses', label: 'Gastos' },
              { id: 'expenses-list', label: 'Gastos' },
              { id: 'edit', label: 'Editar gasto' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/purchases-expenses/expenses/expenses/expense-edit/general/expense-general.component'
              ).then(m => m.ExpenseGeneralComponent),
          },
          {
            path: 'fixed-asset',
            loadComponent: () =>
              import(
                './pages/purchases-expenses/expenses/expenses/expense-edit/fixed-asset/expense-fixed-asset.component'
              ).then(m => m.ExpenseFixedAssetComponent),
          },
          {
            path: 'comprobantes',
            loadComponent: () =>
              import(
                './pages/purchases-expenses/expenses/expenses/expense-edit/comprobantes/expense-comprobantes.component'
              ).then(m => m.ExpenseComprobantesComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/purchases-expenses/expenses/expenses/expense-edit/files/expense-files.component'
              ).then(m => m.ExpenseFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/purchases-expenses/expenses/expenses/expense-edit/action-log/expense-action-log.component'
              ).then(m => m.ExpenseActionLogComponent),
          },
        ],
      },
      {
        path: 'purchases-expenses/purchase-orders/purchase-orders/:purchaseOrderId/edit',
        loadComponent: () =>
          import(
            './pages/purchases-expenses/purchase-orders/purchase-orders/purchase-order-edit/purchase-order-edit.component'
          ).then(m => m.PurchaseOrderEditComponent),
        data: {
          chrome: pageChrome({
            module: PURCHASES_EXPENSES,
            activeMenubarId: 'pxe-ord-purchase-orders',
            title: 'Editar orden de compra',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              PURCHASES_EXPENSES,
              { id: 'orders', label: 'Orden de compra' },
              { id: 'purchase-orders', label: 'Orden de compra' },
              { id: 'edit', label: 'Editar orden de compra' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/purchases-expenses/purchase-orders/purchase-orders/purchase-order-edit/general/purchase-order-general.component'
              ).then(m => m.PurchaseOrderGeneralComponent),
          },
          {
            path: 'quotes',
            loadComponent: () =>
              import(
                './pages/purchases-expenses/purchase-orders/purchase-orders/purchase-order-edit/quotes/purchase-order-quotes.component'
              ).then(m => m.PurchaseOrderQuotesComponent),
          },
          {
            path: 'comprobantes',
            loadComponent: () =>
              import(
                './pages/purchases-expenses/purchase-orders/purchase-orders/purchase-order-edit/comprobantes/purchase-order-comprobantes.component'
              ).then(m => m.PurchaseOrderComprobantesComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/purchases-expenses/purchase-orders/purchase-orders/purchase-order-edit/files/purchase-order-files.component'
              ).then(m => m.PurchaseOrderFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/purchases-expenses/purchase-orders/purchase-orders/purchase-order-edit/action-log/purchase-order-action-log.component'
              ).then(m => m.PurchaseOrderActionLogComponent),
          },
        ],
      },
      {
        path: 'purchases-expenses/purchase-orders/purchase-orders/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/purchases-expenses/purchase-orders/purchase-orders/purchase-order-form/purchase-order-form.component'
          ).then(m => m.PurchaseOrderFormComponent),
        data: {
          chrome: pageChrome({
            module: PURCHASES_EXPENSES,
            activeMenubarId: 'pxe-ord-purchase-orders',
            title: 'Nueva orden de compra',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              PURCHASES_EXPENSES,
              { id: 'orders', label: 'Orden de compra' },
              { id: 'purchase-orders', label: 'Orden de compra' },
              { id: 'new', label: 'Nueva orden de compra' },
            ),
          }),
        },
      },
      {
        path: 'purchases-expenses/requisitions/purchases',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/purchases-expenses/requisitions/purchases/requisitions.component'
          ).then(m => m.RequisitionsComponent),
        data: {
          chrome: pageChrome({
            module: PURCHASES_EXPENSES,
            activeMenubarId: 'pxe-req-purchases',
            title: 'Requisiciones de compra',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              PURCHASES_EXPENSES,
              { id: 'requisitions', label: 'Requisiciones' },
              { id: 'purchases', label: 'Compras' },
            ),
          }),
        },
      },
      {
        path: 'purchases-expenses/requisitions/purchases/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/purchases-expenses/requisitions/purchases/requisition-form/requisition-form.component'
          ).then(m => m.RequisitionFormComponent),
        data: {
          chrome: pageChrome({
            module: PURCHASES_EXPENSES,
            activeMenubarId: 'pxe-req-purchases',
            title: 'Nueva requisición de compra',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              PURCHASES_EXPENSES,
              { id: 'requisitions', label: 'Requisiciones' },
              { id: 'purchases', label: 'Compras' },
              { id: 'new', label: 'Nueva requisición de compra' },
            ),
          }),
        },
      },
      {
        path: 'purchases-expenses/requisitions/purchases/:requisitionId/edit',
        loadComponent: () =>
          import(
            './pages/purchases-expenses/requisitions/purchases/requisition-edit/requisition-edit.component'
          ).then(m => m.RequisitionEditComponent),
        data: {
          chrome: pageChrome({
            module: PURCHASES_EXPENSES,
            activeMenubarId: 'pxe-req-purchases',
            title: 'Editar requisición de compra',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              PURCHASES_EXPENSES,
              { id: 'requisitions', label: 'Requisiciones' },
              { id: 'purchases', label: 'Compras' },
              { id: 'edit', label: 'Editar requisición de compra' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/purchases-expenses/requisitions/purchases/requisition-edit/general/requisition-general.component'
              ).then(m => m.RequisitionGeneralComponent),
          },
          {
            path: 'quotes',
            loadComponent: () =>
              import(
                './pages/purchases-expenses/requisitions/purchases/requisition-edit/quotes/requisition-quotes.component'
              ).then(m => m.RequisitionQuotesComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/purchases-expenses/requisitions/purchases/requisition-edit/files/requisition-files.component'
              ).then(m => m.RequisitionFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/purchases-expenses/requisitions/purchases/requisition-edit/action-log/requisition-action-log.component'
              ).then(m => m.RequisitionActionLogComponent),
          },
        ],
      },
      {
        path: 'purchases-expenses/requisitions/expenses',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/purchases-expenses/requisitions/expenses/requisition-expenses.component'
          ).then(m => m.RequisitionExpensesComponent),
        data: {
          chrome: pageChrome({
            module: PURCHASES_EXPENSES,
            activeMenubarId: 'pxe-req-expenses',
            title: 'Requisiciones de gastos',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              PURCHASES_EXPENSES,
              { id: 'requisitions', label: 'Requisiciones' },
              { id: 'expenses', label: 'Gastos' },
            ),
          }),
        },
      },
      {
        path: 'purchases-expenses/requisitions/expenses/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/purchases-expenses/requisitions/expenses/requisition-form/requisition-form.component'
          ).then(m => m.ExpenseRequisitionFormComponent),
        data: {
          chrome: pageChrome({
            module: PURCHASES_EXPENSES,
            activeMenubarId: 'pxe-req-expenses',
            title: 'Nueva requisición de gastos',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              PURCHASES_EXPENSES,
              { id: 'requisitions', label: 'Requisiciones' },
              { id: 'expenses', label: 'Gastos' },
              { id: 'new', label: 'Nueva requisición de gastos' },
            ),
          }),
        },
      },
      {
        path: 'purchases-expenses/requisitions/expenses/:requisitionId/edit',
        loadComponent: () =>
          import(
            './pages/purchases-expenses/requisitions/expenses/requisition-edit/requisition-edit.component'
          ).then(m => m.ExpenseRequisitionEditComponent),
        data: {
          chrome: pageChrome({
            module: PURCHASES_EXPENSES,
            activeMenubarId: 'pxe-req-expenses',
            title: 'Editar requisición de gastos',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              PURCHASES_EXPENSES,
              { id: 'requisitions', label: 'Requisiciones' },
              { id: 'expenses', label: 'Gastos' },
              { id: 'edit', label: 'Editar requisición de gastos' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/purchases-expenses/requisitions/expenses/requisition-edit/general/requisition-general.component'
              ).then(m => m.ExpenseRequisitionGeneralComponent),
          },
          {
            path: 'comprobantes',
            pathMatch: 'full',
            loadComponent: () =>
              import(
                './pages/purchases-expenses/requisitions/expenses/requisition-edit/comprobantes/requisition-comprobantes.component'
              ).then(m => m.ExpenseRequisitionComprobantesComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/purchases-expenses/requisitions/expenses/requisition-edit/files/requisition-files.component'
              ).then(m => m.ExpenseRequisitionFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/purchases-expenses/requisitions/expenses/requisition-edit/action-log/requisition-action-log.component'
              ).then(m => m.ExpenseRequisitionActionLogComponent),
          },
        ],
      },
      {
        path: 'purchases-expenses/requisitions/advances',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/purchases-expenses/requisitions/advances/requisition-advances.component'
          ).then(m => m.RequisitionAdvancesComponent),
        data: {
          chrome: pageChrome({
            module: PURCHASES_EXPENSES,
            activeMenubarId: 'pxe-req-advances',
            title: 'Anticipos',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              PURCHASES_EXPENSES,
              { id: 'requisitions', label: 'Requisiciones' },
              { id: 'advances', label: 'Anticipos' },
            ),
          }),
        },
      },
      {
        path: 'purchases-expenses/reports/requisitions',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/purchases-expenses/reports/requisitions/requisitions.component'
          ).then(m => m.PurchasesExpensesReportRequisitionsComponent),
        data: {
          chrome: pageChrome({
            module: PURCHASES_EXPENSES,
            activeMenubarId: 'pxe-rep-requisitions',
            title: 'Requisiciones',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              PURCHASES_EXPENSES,
              { id: 'reports', label: 'Reportes' },
              { id: 'requisitions', label: 'Requisiciones' },
            ),
          }),
        },
      },
      {
        path: 'purchases-expenses/reports/purchase-orders',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/purchases-expenses/reports/purchase-orders/purchase-orders.component'
          ).then(m => m.PurchasesExpensesReportPurchaseOrdersComponent),
        data: {
          chrome: pageChrome({
            module: PURCHASES_EXPENSES,
            activeMenubarId: 'pxe-rep-orders',
            title: 'Ordenes de compra',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              PURCHASES_EXPENSES,
              { id: 'reports', label: 'Reportes' },
              { id: 'purchase-orders', label: 'Ordenes de compra' },
            ),
          }),
        },
      },
      {
        path: 'purchases-expenses/reports/expenses',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/purchases-expenses/reports/expenses/expenses.component'
          ).then(m => m.PurchasesExpensesReportExpensesComponent),
        data: {
          chrome: pageChrome({
            module: PURCHASES_EXPENSES,
            activeMenubarId: 'pxe-rep-expenses',
            title: 'Gastos',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              PURCHASES_EXPENSES,
              { id: 'reports', label: 'Reportes' },
              { id: 'expenses', label: 'Gastos' },
            ),
          }),
        },
      },
      {
        path: 'purchases-expenses/reports/advances',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/purchases-expenses/reports/advances/advances.component'
          ).then(m => m.PurchasesExpensesReportAdvancesComponent),
        data: {
          chrome: pageChrome({
            module: PURCHASES_EXPENSES,
            activeMenubarId: 'pxe-rep-advances',
            title: 'Anticipos',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              PURCHASES_EXPENSES,
              { id: 'reports', label: 'Reportes' },
              { id: 'advances', label: 'Anticipos' },
            ),
          }),
        },
      },

      // ── Almacenes ───────────────────────────────────────────────────────
      {
        path: 'warehouses/warehouses',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/warehouses/warehouses/warehouses.component').then(
            m => m.WarehousesComponent,
          ),
        data: {
          chrome: pageChrome({
            module: WAREHOUSES,
            activeMenubarId: 'wh-warehouses',
            title: 'Almacenes',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              WAREHOUSES,
              { id: 'warehouses-list', label: 'Almacenes' },
            ),
          }),
        },
      },
      {
        path: 'warehouses/warehouses/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/warehouses/warehouses/warehouse-form/warehouse-form.component'
          ).then(m => m.WarehouseFormComponent),
        data: {
          chrome: pageChrome({
            module: WAREHOUSES,
            activeMenubarId: 'wh-warehouses',
            title: 'Nuevo almacén',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              WAREHOUSES,
              { id: 'warehouses-list', label: 'Almacenes' },
              { id: 'new', label: 'Nuevo almacén' },
            ),
          }),
        },
      },
      {
        path: 'warehouses/warehouses/:warehouseId/edit',
        loadComponent: () =>
          import(
            './pages/warehouses/warehouses/warehouse-edit/warehouse-edit.component'
          ).then(m => m.WarehouseEditComponent),
        data: {
          chrome: pageChrome({
            module: WAREHOUSES,
            activeMenubarId: 'wh-warehouses',
            title: 'Editar almacén',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              WAREHOUSES,
              { id: 'warehouses-list', label: 'Almacenes' },
              { id: 'edit', label: 'Editar almacén' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/warehouses/warehouses/warehouse-edit/general/warehouse-general.component'
              ).then(m => m.WarehouseGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/warehouses/warehouses/warehouse-edit/files/warehouse-files.component'
              ).then(m => m.WarehouseFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/warehouses/warehouses/warehouse-edit/action-log/warehouse-action-log.component'
              ).then(m => m.WarehouseActionLogComponent),
          },
        ],
      },
      {
        path: 'warehouses/movements/reception',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/warehouses/movements/reception/reception.component'
          ).then(m => m.WarehouseReceptionComponent),
        data: {
          chrome: pageChrome({
            module: WAREHOUSES,
            activeMenubarId: 'wh-mov-reception',
            title: 'Recepción',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              WAREHOUSES,
              { id: 'movements', label: 'Movimientos' },
              { id: 'reception', label: 'Recepción' },
            ),
          }),
        },
      },
      {
        path: 'warehouses/movements/reception/:receptionId',
        loadComponent: () =>
          import(
            './pages/warehouses/movements/reception/reception-edit/reception-edit.component'
          ).then(m => m.WarehouseReceptionDetailComponent),
        data: {
          chrome: pageChrome({
            module: WAREHOUSES,
            activeMenubarId: 'wh-mov-reception',
            title: 'Recepción',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              WAREHOUSES,
              { id: 'movements', label: 'Movimientos' },
              { id: 'reception', label: 'Recepción' },
              { id: 'detail', label: 'Detalle' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/warehouses/movements/reception/reception-edit/general/reception-general.component'
              ).then(m => m.WarehouseReceptionGeneralComponent),
          },
          {
            path: 'comprobantes',
            loadComponent: () =>
              import(
                './pages/warehouses/movements/reception/reception-edit/comprobantes/reception-comprobantes.component'
              ).then(m => m.WarehouseReceptionComprobantesComponent),
          },
          {
            path: 'receive',
            loadComponent: () =>
              import(
                './pages/warehouses/movements/reception/reception-edit/general/reception-general.component'
              ).then(m => m.WarehouseReceptionGeneralComponent),
            data: {
              chrome: pageChrome({
                module: WAREHOUSES,
                activeMenubarId: 'wh-mov-reception',
                title: 'Recepcionar',
                titleBarMode: 'form',
                crumbs: moduleCrumbs(
                  WAREHOUSES,
                  { id: 'movements', label: 'Movimientos' },
                  { id: 'reception', label: 'Recepción' },
                  { id: 'receive', label: 'Recepcionar' },
                ),
              }),
            },
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/warehouses/movements/reception/reception-edit/files/reception-files.component'
              ).then(m => m.WarehouseReceptionFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/warehouses/movements/reception/reception-edit/action-log/reception-action-log.component'
              ).then(m => m.WarehouseReceptionActionLogComponent),
          },
        ],
      },
      {
        path: 'warehouses/movements/entries',
        pathMatch: 'full',
        loadComponent: loadModulePanel,
        data: {
          chrome: pageChrome({
            module: WAREHOUSES,
            activeMenubarId: 'wh-mov-entries',
            title: 'Entradas',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              WAREHOUSES,
              { id: 'movements', label: 'Movimientos' },
              { id: 'entries', label: 'Entradas' },
            ),
          }),
        },
      },
      {
        path: 'warehouses/movements/exits',
        pathMatch: 'full',
        loadComponent: loadModulePanel,
        data: {
          chrome: pageChrome({
            module: WAREHOUSES,
            activeMenubarId: 'wh-mov-exits',
            title: 'Salidas',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              WAREHOUSES,
              { id: 'movements', label: 'Movimientos' },
              { id: 'exits', label: 'Salidas' },
            ),
          }),
        },
      },
      {
        path: 'warehouses/movements/transfers',
        pathMatch: 'full',
        loadComponent: loadModulePanel,
        data: {
          chrome: pageChrome({
            module: WAREHOUSES,
            activeMenubarId: 'wh-mov-transfers',
            title: 'Traspasos',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              WAREHOUSES,
              { id: 'movements', label: 'Movimientos' },
              { id: 'transfers', label: 'Traspasos' },
            ),
          }),
        },
      },
      {
        path: 'warehouses/kardex',
        pathMatch: 'full',
        loadComponent: loadModulePanel,
        data: {
          chrome: pageChrome({
            module: WAREHOUSES,
            activeMenubarId: 'wh-kardex',
            title: 'Kardex',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(WAREHOUSES, {
              id: 'kardex',
              label: 'Kardex',
            }),
          }),
        },
      },
      {
        path: 'warehouses/reports/stock',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/warehouses/reports/stock/stock.component').then(
            m => m.WarehouseReportStockComponent,
          ),
        data: {
          chrome: pageChrome({
            module: WAREHOUSES,
            activeMenubarId: 'wh-rep-stock',
            title: 'Existencias',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              WAREHOUSES,
              { id: 'reports', label: 'Reportes' },
              { id: 'stock', label: 'Existencias' },
            ),
          }),
        },
      },
      {
        path: 'warehouses/reports/movements',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/warehouses/reports/movements/movements.component').then(
            m => m.WarehouseReportMovementsComponent,
          ),
        data: {
          chrome: pageChrome({
            module: WAREHOUSES,
            activeMenubarId: 'wh-rep-movements',
            title: 'Movimientos',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              WAREHOUSES,
              { id: 'reports', label: 'Reportes' },
              { id: 'movements', label: 'Movimientos' },
            ),
          }),
        },
      },

      // ── Control de obra ─────────────────────────────────────────────────
      {
        path: 'construction/projects/projects',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/construction/projects/projects/projects.component').then(
            m => m.ProjectsComponent,
          ),
        data: {
          chrome: pageChrome({
            module: CONSTRUCTION,
            activeMenubarId: 'con-projects',
            title: 'Proyectos',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(CONSTRUCTION, {
              id: 'projects',
              label: 'Proyectos',
            }),
          }),
        },
      },
      {
        path: 'construction/projects/projects/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/construction/projects/projects/project-form/project-form.component'
          ).then(m => m.ProjectFormComponent),
        data: {
          chrome: pageChrome({
            module: CONSTRUCTION,
            activeMenubarId: 'con-projects',
            title: 'Nuevo proyecto',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              CONSTRUCTION,
              { id: 'projects', label: 'Proyectos' },
              { id: 'new', label: 'Nuevo proyecto' },
            ),
          }),
        },
      },

      // ── Proveedores ─────────────────────────────────────────────────────
      {
        path: 'suppliers/suppliers',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/suppliers/suppliers/suppliers.component').then(
            m => m.SuppliersComponent,
          ),
        data: {
          chrome: pageChrome({
            module: SUPPLIERS,
            activeMenubarId: 'sup-suppliers',
            title: 'Proveedores',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(SUPPLIERS, {
              id: 'suppliers-list',
              label: 'Proveedores',
            }),
          }),
        },
      },
      {
        path: 'suppliers/suppliers/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/suppliers/suppliers/supplier-form/supplier-form.component'
          ).then(m => m.SupplierFormComponent),
        data: {
          chrome: pageChrome({
            module: SUPPLIERS,
            activeMenubarId: 'sup-suppliers',
            title: 'Nuevo proveedor',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              SUPPLIERS,
              { id: 'suppliers-list', label: 'Proveedores' },
              { id: 'new', label: 'Nuevo proveedor' },
            ),
          }),
        },
      },
      {
        path: 'suppliers/suppliers/:supplierId/edit',
        loadComponent: () =>
          import(
            './pages/suppliers/suppliers/supplier-edit/supplier-edit.component'
          ).then(m => m.SupplierEditComponent),
        data: {
          chrome: pageChrome({
            module: SUPPLIERS,
            activeMenubarId: 'sup-suppliers',
            title: 'Editar proveedor',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              SUPPLIERS,
              { id: 'suppliers-list', label: 'Proveedores' },
              { id: 'edit', label: 'Editar proveedor' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/suppliers/suppliers/supplier-edit/general/supplier-general.component'
              ).then(m => m.SupplierGeneralComponent),
            children: [
              { path: '', pathMatch: 'full', redirectTo: 'general' },
              {
                path: 'general',
                loadComponent: () =>
                  import(
                    './pages/suppliers/suppliers/supplier-edit/general/general/supplier-general-details.component'
                  ).then(m => m.SupplierGeneralDetailsComponent),
              },
            ],
              },
              {
                path: 'bank-accounts',
                loadComponent: () =>
                  import(
                    './pages/suppliers/suppliers/supplier-edit/general/bank-accounts/supplier-bank-accounts.component'
                  ).then(m => m.SupplierBankAccountsComponent),
          },
          {
            path: 'digital-file',
            loadComponent: () =>
              import(
                './pages/suppliers/suppliers/supplier-edit/digital-file/supplier-digital-file.component'
              ).then(m => m.SupplierDigitalFileComponent),
          },
          {
            path: 'contacts',
            loadComponent: () =>
              import(
                './pages/suppliers/suppliers/supplier-edit/contacts/supplier-contacts.component'
              ).then(m => m.SupplierContactsComponent),
          },
          {
            path: 'accounting-accounts',
            loadComponent: () =>
              import(
                './pages/suppliers/suppliers/supplier-edit/accounting-accounts/supplier-accounting-accounts.component'
              ).then(m => m.SupplierAccountingAccountsComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/suppliers/suppliers/supplier-edit/files/supplier-files.component'
              ).then(m => m.SupplierFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/suppliers/suppliers/supplier-edit/action-log/supplier-action-log.component'
              ).then(m => m.SupplierActionLogComponent),
          },
        ],
      },
      {
        path: 'suppliers/catalogs/service-type',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/suppliers/catalogs/service-type/service-type.component'
          ).then(m => m.ServiceTypeComponent),
        data: {
          chrome: pageChrome({
            module: SUPPLIERS,
            activeMenubarId: 'sup-cat-service-type',
            title: 'Tipo de servicio',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              SUPPLIERS,
              { id: 'sup-catalogs', label: 'Catálogos' },
              { id: 'sup-cat-service-type', label: 'Tipo de servicio' },
            ),
          }),
        },
      },
      {
        path: 'suppliers/catalogs/service-type/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/suppliers/catalogs/service-type/service-type-form/service-type-form.component'
          ).then(m => m.ServiceTypeFormComponent),
        data: {
          chrome: pageChrome({
            module: SUPPLIERS,
            activeMenubarId: 'sup-cat-service-type',
            title: 'Nuevo tipo de servicio',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              SUPPLIERS,
              { id: 'sup-catalogs', label: 'Catálogos' },
              { id: 'sup-cat-service-type', label: 'Tipo de servicio' },
              { id: 'new', label: 'Nuevo tipo de servicio' },
            ),
          }),
        },
      },
      {
        path: 'suppliers/catalogs/service-type/edit',
        loadComponent: () =>
          import(
            './pages/suppliers/catalogs/service-type/service-type-edit/service-type-edit.component'
          ).then(m => m.ServiceTypeEditComponent),
        data: {
          chrome: pageChrome({
            module: SUPPLIERS,
            activeMenubarId: 'sup-cat-service-type',
            title: 'Editar tipo de servicio',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              SUPPLIERS,
              { id: 'sup-catalogs', label: 'Catálogos' },
              { id: 'sup-cat-service-type', label: 'Tipo de servicio' },
              { id: 'edit', label: 'Editar tipo de servicio' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/suppliers/catalogs/service-type/service-type-edit/general/general.component'
              ).then(m => m.ServiceTypeGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/suppliers/catalogs/service-type/service-type-edit/files/files.component'
              ).then(m => m.ServiceTypeFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/suppliers/catalogs/service-type/service-type-edit/action-log/action-log.component'
              ).then(m => m.ServiceTypeActionLogComponent),
          },
        ],
      },
      {
        path: 'suppliers/registration-requests',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/suppliers/registration-requests/registration-requests.component'
          ).then(m => m.SupplierRegistrationRequestsComponent),
        data: {
          chrome: pageChrome({
            module: SUPPLIERS,
            activeMenubarId: 'sup-registration-requests',
            title: 'Solicitudes de registro',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              SUPPLIERS,
              { id: 'sup-suppliers-menu', label: 'Proveedores' },
              {
                id: 'sup-registration-requests',
                label: 'Solicitudes de registro',
              },
            ),
          }),
        },
      },
      {
        path: 'suppliers/registration-requests/:requestId/edit',
        loadComponent: () =>
          import(
            './pages/suppliers/registration-requests/registration-request-edit/registration-request-edit.component'
          ).then(m => m.RegistrationRequestEditComponent),
        data: {
          chrome: pageChrome({
            module: SUPPLIERS,
            activeMenubarId: 'sup-registration-requests',
            title: 'Editar solicitud',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              SUPPLIERS,
              { id: 'sup-suppliers-menu', label: 'Proveedores' },
              {
                id: 'sup-registration-requests',
                label: 'Solicitudes de registro',
              },
              { id: 'edit', label: 'Editar solicitud' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/suppliers/registration-requests/registration-request-edit/general/registration-request-general.component'
              ).then(m => m.RegistrationRequestGeneralComponent),
            children: [
              { path: '', pathMatch: 'full', redirectTo: 'general' },
              {
                path: 'general',
                loadComponent: () =>
                  import(
                    './pages/suppliers/registration-requests/registration-request-edit/general/general/registration-request-general-details.component'
                  ).then(m => m.RegistrationRequestGeneralDetailsComponent),
              },
              {
                path: 'contacts',
                loadComponent: () =>
                  import(
                    './pages/suppliers/registration-requests/registration-request-edit/general/contacts/registration-request-contacts.component'
                  ).then(m => m.RegistrationRequestContactsComponent),
              },
              {
                path: 'payment',
                loadComponent: () =>
                  import(
                    './pages/suppliers/registration-requests/registration-request-edit/general/payment/registration-request-payment.component'
                  ).then(m => m.RegistrationRequestPaymentComponent),
              },
              {
                path: 'documents',
                loadComponent: () =>
                  import(
                    './pages/suppliers/registration-requests/registration-request-edit/general/documents/registration-request-documents.component'
                  ).then(m => m.RegistrationRequestDocumentsComponent),
              },
            ],
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/suppliers/registration-requests/registration-request-edit/files/registration-request-files.component'
              ).then(m => m.RegistrationRequestFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/suppliers/registration-requests/registration-request-edit/action-log/registration-request-action-log.component'
              ).then(m => m.RegistrationRequestActionLogComponent),
          },
        ],
      },
      {
        path: 'suppliers/payment-scheduling/payment-scheduling',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/suppliers/payment-scheduling/payment-scheduling.component'
          ).then(m => m.PaymentSchedulingComponent),
        data: {
          chrome: pageChrome({
            module: SUPPLIERS,
            activeMenubarId: 'sup-schedule',
            title: 'Programación de pagos',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(SUPPLIERS, {
              id: 'payment-scheduling',
              label: 'Programación de pagos',
            }),
          }),
        },
      },
      {
        path:
          'suppliers/payment-scheduling/payment-scheduling/:companyId/invoices',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/suppliers/payment-scheduling/invoices/payment-scheduling-invoices.component'
          ).then(m => m.PaymentSchedulingInvoicesComponent),
        data: {
          chrome: pageChrome({
            module: SUPPLIERS,
            activeMenubarId: 'sup-schedule',
            title: 'Facturas por pagar',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              SUPPLIERS,
              {
                id: 'payment-scheduling',
                label: 'Programación de pagos',
              },
              { id: 'invoices', label: 'Facturas por pagar' },
            ),
          }),
        },
      },
      {
        path: 'suppliers/payments/payments',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/suppliers/payments/payments-list/payments-list.component'
          ).then(m => m.SupplierPaymentsListComponent),
        data: {
          chrome: pageChrome({
            module: SUPPLIERS,
            activeMenubarId: 'sup-pay-payments',
            title: 'Pagos',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              SUPPLIERS,
              { id: 'payments', label: 'Pagos' },
              { id: 'payments-list', label: 'Pagos' },
            ),
          }),
        },
      },
      {
        path: 'suppliers/payments/programming-application',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/suppliers/payments/payments/payments.component').then(
            m => m.SupplierPaymentsComponent,
          ),
        data: {
          chrome: pageChrome({
            module: SUPPLIERS,
            activeMenubarId: 'sup-pay-application',
            title: 'Aplicación de programación',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              SUPPLIERS,
              { id: 'payments', label: 'Pagos' },
              { id: 'programming-application', label: 'Aplicación de programación' },
            ),
          }),
        },
      },
      {
        path: 'suppliers/payments/programming-application/:applicationId/edit',
        loadComponent: () =>
          import(
            './pages/suppliers/payments/programming-application-edit/programming-application-edit.component'
          ).then(m => m.ProgrammingApplicationEditComponent),
        data: {
          chrome: pageChrome({
            module: SUPPLIERS,
            activeMenubarId: 'sup-pay-application',
            title: 'Editar aplicación de programación',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              SUPPLIERS,
              { id: 'payments', label: 'Pagos' },
              {
                id: 'programming-application',
                label: 'Aplicación de programación',
              },
              { id: 'edit', label: 'Editar aplicación de programación' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/suppliers/payments/programming-application-edit/general/programming-application-general.component'
              ).then(m => m.ProgrammingApplicationGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/suppliers/payments/programming-application-edit/files/programming-application-files.component'
              ).then(m => m.ProgrammingApplicationFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/suppliers/payments/programming-application-edit/action-log/programming-application-action-log.component'
              ).then(m => m.ProgrammingApplicationActionLogComponent),
          },
        ],
      },

      {
        path: 'suppliers/reports/summary',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/suppliers/reports/summary/summary.component').then(
            m => m.SupplierReportSummaryComponent,
          ),
        data: {
          chrome: pageChrome({
            module: SUPPLIERS,
            activeMenubarId: 'sup-rep-summary',
            title: 'Resumen',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              SUPPLIERS,
              { id: 'reports', label: 'Reportes' },
              { id: 'summary', label: 'Resumen' },
            ),
          }),
        },
      },
      {
        path: 'suppliers/reports/account-statement',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/suppliers/reports/account-statement/account-statement.component'
          ).then(m => m.SupplierReportAccountStatementComponent),
        data: {
          chrome: pageChrome({
            module: SUPPLIERS,
            activeMenubarId: 'sup-rep-account',
            title: 'Estado de cuenta',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              SUPPLIERS,
              { id: 'reports', label: 'Reportes' },
              { id: 'account-statement', label: 'Estado de cuenta' },
            ),
          }),
        },
      },
      {
        path: 'suppliers/reports/payment-scheduling',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/suppliers/reports/payment-scheduling/payment-scheduling.component'
          ).then(m => m.SupplierReportPaymentSchedulingComponent),
        data: {
          chrome: pageChrome({
            module: SUPPLIERS,
            activeMenubarId: 'sup-rep-schedule',
            title: 'Programación de pagos',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              SUPPLIERS,
              { id: 'reports', label: 'Reportes' },
              { id: 'payment-scheduling', label: 'Programación de pagos' },
            ),
          }),
        },
      },
      {
        path: 'suppliers/reports/payments',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/suppliers/reports/payments/payments.component').then(
            m => m.SupplierReportPaymentsComponent,
          ),
        data: {
          chrome: pageChrome({
            module: SUPPLIERS,
            activeMenubarId: 'sup-rep-payments',
            title: 'Pagos',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              SUPPLIERS,
              { id: 'reports', label: 'Reportes' },
              { id: 'payments', label: 'Pagos' },
            ),
          }),
        },
      },

      // ── Ventas, Clientes, Titulación y Auditoría ────────────────────────
      {
        path: 'sales/sales',
        pathMatch: 'full',
        loadComponent: loadModulePanel,
        data: {
          chrome: pageChrome({
            module: SALES,
            activeMenubarId: 'sal-sales',
            title: 'Ventas',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(SALES, {
              id: 'sal-sales',
              label: 'Ventas',
            }),
          }),
        },
      },
      {
        path: 'sales/quotes',
        pathMatch: 'full',
        loadComponent: loadModulePanel,
        data: {
          chrome: pageChrome({
            module: SALES,
            activeMenubarId: 'sal-quotes',
            title: 'Cotizaciones',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(SALES, {
              id: 'sal-quotes',
              label: 'Cotizaciones',
            }),
          }),
        },
      },
      {
        path: 'clients/clients',
        pathMatch: 'full',
        loadComponent: loadModulePanel,
        data: {
          chrome: pageChrome({
            module: CLIENTS,
            activeMenubarId: 'cli-clients',
            title: 'Clientes',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(CLIENTS, {
              id: 'cli-clients',
              label: 'Clientes',
            }),
          }),
        },
      },
      {
        path: 'sales/reports/sales',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/sales/reports/sales/sales.component').then(
            m => m.SalesReportSalesComponent,
          ),
        data: {
          chrome: pageChrome({
            module: SALES,
            activeMenubarId: 'sal-rep-sales',
            title: 'Ventas',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              SALES,
              { id: 'reports', label: 'Reportes' },
              { id: 'sales', label: 'Ventas' },
            ),
          }),
        },
      },
      {
        path: 'sales/reports/blocks',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/sales/reports/blocks/blocks.component').then(
            m => m.SalesReportBlocksComponent,
          ),
        data: {
          chrome: pageChrome({
            module: SALES,
            activeMenubarId: 'sal-rep-blocks',
            title: 'Bloqueos',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              SALES,
              { id: 'reports', label: 'Reportes' },
              { id: 'blocks', label: 'Bloqueos' },
            ),
          }),
        },
      },
      {
        path: 'sales/reports/quotes',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/sales/reports/quotes/quotes.component').then(
            m => m.SalesReportQuotesComponent,
          ),
        data: {
          chrome: pageChrome({
            module: SALES,
            activeMenubarId: 'sal-rep-quotes',
            title: 'Cotizaciones',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              SALES,
              { id: 'reports', label: 'Reportes' },
              { id: 'quotes', label: 'Cotizaciones' },
            ),
          }),
        },
      },
      {
        path: 'clients/reports/summary',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/clients/reports/summary/summary.component').then(
            m => m.ClientReportSummaryComponent,
          ),
        data: {
          chrome: pageChrome({
            module: CLIENTS,
            activeMenubarId: 'cli-rep-summary',
            title: 'Resumen',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              CLIENTS,
              { id: 'reports', label: 'Reportes' },
              { id: 'summary', label: 'Resumen' },
            ),
          }),
        },
      },
      {
        path: 'clients/reports/account-statement',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/clients/reports/account-statement/account-statement.component'
          ).then(m => m.ClientReportAccountStatementComponent),
        data: {
          chrome: pageChrome({
            module: CLIENTS,
            activeMenubarId: 'cli-rep-account',
            title: 'Estado de cuenta',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              CLIENTS,
              { id: 'reports', label: 'Reportes' },
              { id: 'account-statement', label: 'Estado de cuenta' },
            ),
          }),
        },
      },
      {
        path: 'clients/reports/collection',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/clients/reports/collection/collection.component').then(
            m => m.ClientReportCollectionComponent,
          ),
        data: {
          chrome: pageChrome({
            module: CLIENTS,
            activeMenubarId: 'cli-rep-collection',
            title: 'Cobranza',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              CLIENTS,
              { id: 'reports', label: 'Reportes' },
              { id: 'collection', label: 'Cobranza' },
            ),
          }),
        },
      },
      {
        path: 'titulation/files/files',
        pathMatch: 'full',
        loadComponent: loadModulePanel,
        data: {
          chrome: pageChrome({
            module: TITULATION,
            activeMenubarId: 'tit-fil-files',
            title: 'Expedientes',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              TITULATION,
              { id: 'tit-files', label: 'Expedientes' },
              { id: 'tit-fil-files', label: 'Expedientes' },
            ),
          }),
        },
      },
      {
        path: 'audit/banks',
        pathMatch: 'full',
        loadComponent: loadModulePanel,
        data: {
          chrome: pageChrome({
            module: AUDIT,
            activeMenubarId: 'aud-banks',
            title: 'Bancos',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(AUDIT, {
              id: 'aud-banks',
              label: 'Bancos',
            }),
          }),
        },
      },

      // ── Recursos humanos ────────────────────────────────────────────────
      {
        path: 'human-resources/employees/employees',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/human-resources/employees/employees/employees.component').then(
            m => m.HrEmployeesComponent,
          ),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-employees',
            title: 'Empleados',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              HR,
              { id: 'employees', label: 'Empleados' },
              { id: 'employees-list', label: 'Empleados' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/employees/employees/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/human-resources/employees/employees/employee-form/employee-form.component'
          ).then(m => m.HrEmployeeFormComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-employees',
            title: 'Nuevo empleado',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'employees', label: 'Empleados' },
              { id: 'employees-list', label: 'Empleados' },
              { id: 'new', label: 'Nuevo empleado' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/employees/employees/:employeeId/edit',
        loadComponent: () =>
          import(
            './pages/human-resources/employees/employees/employee-edit/employee-edit.component'
          ).then(m => m.HrEmployeeEditComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-employees',
            title: 'Editar empleado',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'employees', label: 'Empleados' },
              { id: 'employees-list', label: 'Empleados' },
              { id: 'edit', label: 'Editar empleado' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/human-resources/employees/employees/employee-edit/general/employee-general.component'
              ).then(m => m.HrEmployeeGeneralComponent),
          },
          {
            path: 'information',
            loadComponent: () =>
              import(
                './pages/human-resources/employees/employees/employee-edit/information/employee-information.component'
              ).then(m => m.HrEmployeeInformationComponent),
          },
          {
            path: 'movements',
            loadComponent: () =>
              import(
                './pages/human-resources/employees/employees/employee-edit/movements/employee-movements.component'
              ).then(m => m.HrEmployeeMovementsComponent),
          },
          {
            path: 'payroll',
            loadComponent: () =>
              import(
                './pages/human-resources/employees/employees/employee-edit/payroll/employee-payroll.component'
              ).then(m => m.HrEmployeePayrollTabComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/human-resources/employees/employees/employee-edit/files/employee-files.component'
              ).then(m => m.HrEmployeeFilesComponent),
          },
          {
            path: 'digital-file',
            loadComponent: () =>
              import(
                './pages/human-resources/employees/employees/employee-edit/digital-file/employee-digital-file.component'
              ).then(m => m.HrEmployeeDigitalFileComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/human-resources/employees/employees/employee-edit/action-log/employee-action-log.component'
              ).then(m => m.HrEmployeeActionLogComponent),
          },
        ],
      },
      {
        path: 'human-resources/payroll/payroll',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/human-resources/payroll/payroll/payroll.component').then(
            m => m.HrPayrollComponent,
          ),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-payroll',
            title: 'Nóminas',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              HR,
              { id: 'payroll', label: 'Nóminas' },
              { id: 'payroll-list', label: 'Nóminas' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/catalogs/positions',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/positions/positions.component'
          ).then(m => m.HrPositionsComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-positions',
            title: 'Puestos',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'positions', label: 'Puestos' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/catalogs/positions/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/positions/position-form/position-form.component'
          ).then(m => m.HrPositionFormComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-positions',
            title: 'Nuevo puesto',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'positions', label: 'Puestos' },
              { id: 'new', label: 'Nuevo puesto' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/catalogs/positions/:positionId/edit',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/positions/position-edit/position-edit.component'
          ).then(m => m.HrPositionEditComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-positions',
            title: 'Editar puesto',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'positions', label: 'Puestos' },
              { id: 'edit', label: 'Editar puesto' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/positions/position-edit/general/general.component'
              ).then(m => m.HrPositionGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/positions/position-edit/files/files.component'
              ).then(m => m.HrPositionFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/positions/position-edit/action-log/action-log.component'
              ).then(m => m.HrPositionActionLogComponent),
          },
        ],
      },
      {
        path: 'human-resources/catalogs/departments',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/departments/departments.component'
          ).then(m => m.HrDepartmentsComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-departments',
            title: 'Departamentos',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'departments', label: 'Departamentos' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/catalogs/departments/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/departments/department-form/department-form.component'
          ).then(m => m.HrDepartmentFormComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-departments',
            title: 'Nuevo departamento',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'departments', label: 'Departamentos' },
              { id: 'new', label: 'Nuevo departamento' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/catalogs/departments/:departmentId/edit',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/departments/department-edit/department-edit.component'
          ).then(m => m.HrDepartmentEditComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-departments',
            title: 'Editar departamento',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'departments', label: 'Departamentos' },
              { id: 'edit', label: 'Editar departamento' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/departments/department-edit/general/general.component'
              ).then(m => m.HrDepartmentGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/departments/department-edit/files/files.component'
              ).then(m => m.HrDepartmentFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/departments/department-edit/action-log/action-log.component'
              ).then(m => m.HrDepartmentActionLogComponent),
          },
        ],
      },
      {
        path: 'human-resources/catalogs/perceptions',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/perceptions/perceptions.component'
          ).then(m => m.HrPerceptionsComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-perceptions',
            title: 'Percepciones',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'perceptions', label: 'Percepciones' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/catalogs/perceptions/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/perceptions/perception-form/perception-form.component'
          ).then(m => m.HrPerceptionFormComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-perceptions',
            title: 'Nueva percepción',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'perceptions', label: 'Percepciones' },
              { id: 'new', label: 'Nueva percepción' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/catalogs/perceptions/:perceptionId/edit',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/perceptions/perception-edit/perception-edit.component'
          ).then(m => m.HrPerceptionEditComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-perceptions',
            title: 'Editar percepción',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'perceptions', label: 'Percepciones' },
              { id: 'edit', label: 'Editar percepción' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/perceptions/perception-edit/general/general.component'
              ).then(m => m.HrPerceptionGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/perceptions/perception-edit/files/files.component'
              ).then(m => m.HrPerceptionFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/perceptions/perception-edit/action-log/action-log.component'
              ).then(m => m.HrPerceptionActionLogComponent),
          },
        ],
      },
      {
        path: 'human-resources/catalogs/deductions',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/deductions/deductions.component'
          ).then(m => m.HrDeductionsComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-deductions',
            title: 'Deducciones',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'deductions', label: 'Deducciones' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/catalogs/deductions/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/deductions/deduction-form/deduction-form.component'
          ).then(m => m.HrDeductionFormComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-deductions',
            title: 'Nueva deducción',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'deductions', label: 'Deducciones' },
              { id: 'new', label: 'Nueva deducción' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/catalogs/deductions/:deductionId/edit',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/deductions/deduction-edit/deduction-edit.component'
          ).then(m => m.HrDeductionEditComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-deductions',
            title: 'Editar deducción',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'deductions', label: 'Deducciones' },
              { id: 'edit', label: 'Editar deducción' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/deductions/deduction-edit/general/general.component'
              ).then(m => m.HrDeductionGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/deductions/deduction-edit/files/files.component'
              ).then(m => m.HrDeductionFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/deductions/deduction-edit/action-log/action-log.component'
              ).then(m => m.HrDeductionActionLogComponent),
          },
        ],
      },
      {
        path: 'human-resources/catalogs/contpai-nominas/benefit-types',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/contpai-nominas/benefit-types/benefit-types.component'
          ).then(m => m.HrBenefitTypesComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-benefit-types',
            title: 'Tipo de prestación',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'contpai-nominas', label: 'Contpaqi Nominas' },
              { id: 'benefit-types', label: 'Tipo de prestación' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/catalogs/contpai-nominas/benefit-types/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/contpai-nominas/benefit-types/benefit-type-form/benefit-type-form.component'
          ).then(m => m.HrBenefitTypeFormComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-benefit-types',
            title: 'Nuevo tipo de prestación',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'contpai-nominas', label: 'Contpaqi Nominas' },
              { id: 'benefit-types', label: 'Tipo de prestación' },
              { id: 'new', label: 'Nuevo tipo de prestación' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/catalogs/contpai-nominas/benefit-types/:benefitTypeId/edit',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/contpai-nominas/benefit-types/benefit-type-edit/benefit-type-edit.component'
          ).then(m => m.HrBenefitTypeEditComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-benefit-types',
            title: 'Editar tipo de prestación',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'contpai-nominas', label: 'Contpaqi Nominas' },
              { id: 'benefit-types', label: 'Tipo de prestación' },
              { id: 'edit', label: 'Editar tipo de prestación' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/contpai-nominas/benefit-types/benefit-type-edit/general/general.component'
              ).then(m => m.HrBenefitTypeGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/contpai-nominas/benefit-types/benefit-type-edit/files/files.component'
              ).then(m => m.HrBenefitTypeFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/contpai-nominas/benefit-types/benefit-type-edit/action-log/action-log.component'
              ).then(m => m.HrBenefitTypeActionLogComponent),
          },
        ],
      },
      {
        path: 'human-resources/catalogs/contpai-nominas/payment-bases',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/contpai-nominas/payment-bases/payment-bases.component'
          ).then(m => m.HrPaymentBasesComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-payment-bases',
            title: 'Base de pago',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'contpai-nominas', label: 'Contpaqi Nominas' },
              { id: 'payment-bases', label: 'Base de pago' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/catalogs/contpai-nominas/payment-bases/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/contpai-nominas/payment-bases/payment-base-form/payment-base-form.component'
          ).then(m => m.HrPaymentBaseFormComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-payment-bases',
            title: 'Nueva base de pago',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'contpai-nominas', label: 'Contpaqi Nominas' },
              { id: 'payment-bases', label: 'Base de pago' },
              { id: 'new', label: 'Nueva base de pago' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/catalogs/contpai-nominas/payment-bases/:paymentBaseId/edit',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/contpai-nominas/payment-bases/payment-base-edit/payment-base-edit.component'
          ).then(m => m.HrPaymentBaseEditComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-payment-bases',
            title: 'Editar base de pago',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'contpai-nominas', label: 'Contpaqi Nominas' },
              { id: 'payment-bases', label: 'Base de pago' },
              { id: 'edit', label: 'Editar base de pago' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/contpai-nominas/payment-bases/payment-base-edit/general/general.component'
              ).then(m => m.HrPaymentBaseGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/contpai-nominas/payment-bases/payment-base-edit/files/files.component'
              ).then(m => m.HrPaymentBaseFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/contpai-nominas/payment-bases/payment-base-edit/action-log/action-log.component'
              ).then(m => m.HrPaymentBaseActionLogComponent),
          },
        ],
      },
      {
        path: 'human-resources/catalogs/payment-periods',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/payment-periods/payment-periods.component'
          ).then(m => m.HrPaymentPeriodsComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-payment-periods',
            title: 'Periodos de pago',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'payment-periods', label: 'Periodos de pago' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/catalogs/payment-periods/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/payment-periods/payment-period-form/payment-period-form.component'
          ).then(m => m.HrPaymentPeriodFormComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-payment-periods',
            title: 'Nuevo periodo de pago',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'payment-periods', label: 'Periodos de pago' },
              { id: 'new', label: 'Nuevo periodo de pago' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/catalogs/payment-periods/:paymentPeriodId/edit',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/payment-periods/payment-period-edit/payment-period-edit.component'
          ).then(m => m.HrPaymentPeriodEditComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-payment-periods',
            title: 'Editar periodo de pago',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'payment-periods', label: 'Periodos de pago' },
              { id: 'edit', label: 'Editar periodo de pago' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/payment-periods/payment-period-edit/general/general.component'
              ).then(m => m.HrPaymentPeriodGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/payment-periods/payment-period-edit/files/files.component'
              ).then(m => m.HrPaymentPeriodFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/payment-periods/payment-period-edit/action-log/action-log.component'
              ).then(m => m.HrPaymentPeriodActionLogComponent),
          },
        ],
      },
      {
        path: 'human-resources/catalogs/contpai-nominas/shifts',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/contpai-nominas/shifts/shifts.component'
          ).then(m => m.HrShiftsComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-shifts',
            title: 'Turnos',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'contpai-nominas', label: 'Contpaqi Nominas' },
              { id: 'shifts', label: 'Turnos' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/catalogs/contpai-nominas/shifts/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/contpai-nominas/shifts/shift-form/shift-form.component'
          ).then(m => m.HrShiftFormComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-shifts',
            title: 'Nuevo turno',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'contpai-nominas', label: 'Contpaqi Nominas' },
              { id: 'shifts', label: 'Turnos' },
              { id: 'new', label: 'Nuevo turno' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/catalogs/contpai-nominas/shifts/:shiftId/edit',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/contpai-nominas/shifts/shift-edit/shift-edit.component'
          ).then(m => m.HrShiftEditComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-shifts',
            title: 'Editar turno',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'contpai-nominas', label: 'Contpaqi Nominas' },
              { id: 'shifts', label: 'Turnos' },
              { id: 'edit', label: 'Editar turno' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/contpai-nominas/shifts/shift-edit/general/general.component'
              ).then(m => m.HrShiftGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/contpai-nominas/shifts/shift-edit/files/files.component'
              ).then(m => m.HrShiftFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/contpai-nominas/shifts/shift-edit/action-log/action-log.component'
              ).then(m => m.HrShiftActionLogComponent),
          },
        ],
      },
      {
        path: 'human-resources/catalogs/contpai-nominas/workday-types',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/contpai-nominas/workday-types/workday-types.component'
          ).then(m => m.HrWorkdayTypesComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-workday-types',
            title: 'Tipo de jornada',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'contpai-nominas', label: 'Contpaqi Nominas' },
              { id: 'workday-types', label: 'Tipo de jornada' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/catalogs/contpai-nominas/workday-types/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/contpai-nominas/workday-types/workday-type-form/workday-type-form.component'
          ).then(m => m.HrWorkdayTypeFormComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-workday-types',
            title: 'Nuevo tipo de jornada',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'contpai-nominas', label: 'Contpaqi Nominas' },
              { id: 'workday-types', label: 'Tipo de jornada' },
              { id: 'new', label: 'Nuevo tipo de jornada' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/catalogs/contpai-nominas/workday-types/:workdayTypeId/edit',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/contpai-nominas/workday-types/workday-type-edit/workday-type-edit.component'
          ).then(m => m.HrWorkdayTypeEditComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-workday-types',
            title: 'Editar tipo de jornada',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'contpai-nominas', label: 'Contpaqi Nominas' },
              { id: 'workday-types', label: 'Tipo de jornada' },
              { id: 'edit', label: 'Editar tipo de jornada' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/contpai-nominas/workday-types/workday-type-edit/general/general.component'
              ).then(m => m.HrWorkdayTypeGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/contpai-nominas/workday-types/workday-type-edit/files/files.component'
              ).then(m => m.HrWorkdayTypeFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/contpai-nominas/workday-types/workday-type-edit/action-log/action-log.component'
              ).then(m => m.HrWorkdayTypeActionLogComponent),
          },
        ],
      },
      {
        path: 'human-resources/catalogs/locations',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/locations/locations.component'
          ).then(m => m.HrLocationsComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-locations',
            title: 'Ubicaciones',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'locations', label: 'Ubicaciones' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/catalogs/locations/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/locations/location-form/location-form.component'
          ).then(m => m.HrLocationFormComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-locations',
            title: 'Nueva ubicación',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'locations', label: 'Ubicaciones' },
              { id: 'new', label: 'Nueva ubicación' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/catalogs/locations/:locationId/edit',
        loadComponent: () =>
          import(
            './pages/human-resources/catalogs/locations/location-edit/location-edit.component'
          ).then(m => m.HrLocationEditComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-cat-locations',
            title: 'Editar ubicación',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'hr-catalogs', label: 'Catálogos' },
              { id: 'locations', label: 'Ubicaciones' },
              { id: 'edit', label: 'Editar ubicación' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/locations/location-edit/general/general.component'
              ).then(m => m.HrLocationGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/locations/location-edit/files/files.component'
              ).then(m => m.HrLocationFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/human-resources/catalogs/locations/location-edit/action-log/action-log.component'
              ).then(m => m.HrLocationActionLogComponent),
          },
        ],
      },
      {
        path: 'human-resources/reports/employees',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/human-resources/reports/employees/employees.component'
          ).then(m => m.HrReportEmployeesComponent),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-rep-employees',
            title: 'Empleados',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'reports', label: 'Reportes' },
              { id: 'employees', label: 'Empleados' },
            ),
          }),
        },
      },
      {
        path: 'human-resources/reports/payroll',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/human-resources/reports/payroll/payroll.component').then(
            m => m.HrReportPayrollComponent,
          ),
        data: {
          chrome: pageChrome({
            module: HR,
            activeMenubarId: 'hr-rep-payroll',
            title: 'Nóminas',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              HR,
              { id: 'reports', label: 'Reportes' },
              { id: 'payroll', label: 'Nóminas' },
            ),
          }),
        },
      },

      // ── Activo fijo ─────────────────────────────────────────────────────
      {
        path: 'fixed-assets/catalogs/types',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/fixed-assets/catalogs/types/types.component').then(
            m => m.FixedAssetTypesComponent,
          ),
        data: {
          chrome: pageChrome({
            module: FIXED_ASSETS,
            activeMenubarId: 'fa-cat-types',
            title: 'Tipos',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              FIXED_ASSETS,
              { id: 'catalogs', label: 'Catálogos' },
              { id: 'types', label: 'Tipos' },
            ),
          }),
        },
      },
      {
        path: 'fixed-assets/catalogs/types/new',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/fixed-assets/catalogs/types/type-form/type-form.component').then(
            m => m.FixedAssetTypeFormComponent,
          ),
        data: {
          chrome: pageChrome({
            module: FIXED_ASSETS,
            activeMenubarId: 'fa-cat-types',
            title: 'Nuevo tipo',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              FIXED_ASSETS,
              { id: 'catalogs', label: 'Catálogos' },
              { id: 'types', label: 'Tipos' },
              { id: 'new', label: 'Nuevo tipo' },
            ),
          }),
        },
      },
      {
        path: 'fixed-assets/catalogs/types/:typeId/edit',
        loadComponent: () =>
          import('./pages/fixed-assets/catalogs/types/type-edit/type-edit.component').then(
            m => m.FixedAssetTypeEditComponent,
          ),
        data: {
          chrome: pageChrome({
            module: FIXED_ASSETS,
            activeMenubarId: 'fa-cat-types',
            title: 'Editar tipo',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              FIXED_ASSETS,
              { id: 'catalogs', label: 'Catálogos' },
              { id: 'types', label: 'Tipos' },
              { id: 'edit', label: 'Editar tipo' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/fixed-assets/catalogs/types/type-edit/general/general.component'
              ).then(m => m.FixedAssetTypeGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/fixed-assets/catalogs/types/type-edit/files/files.component'
              ).then(m => m.FixedAssetTypeFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/fixed-assets/catalogs/types/type-edit/action-log/action-log.component'
              ).then(m => m.FixedAssetTypeActionLogComponent),
          },
        ],
      },
      {
        path: 'fixed-assets/assets/assets/new',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/fixed-assets/assets/assets/asset-form/asset-form.component').then(
            m => m.FixedAssetFormComponent,
          ),
        data: {
          chrome: pageChrome({
            module: FIXED_ASSETS,
            activeMenubarId: 'fa-assets',
            title: 'Nuevo activo',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              FIXED_ASSETS,
              { id: 'assets', label: 'Activos' },
              { id: 'assets-list', label: 'Activos' },
              { id: 'new', label: 'Nuevo activo' },
            ),
          }),
        },
      },
      {
        path: 'fixed-assets/assets/assets/:assetId/edit',
        loadComponent: () =>
          import('./pages/fixed-assets/assets/assets/asset-edit/asset-edit.component').then(
            m => m.FixedAssetEditComponent,
          ),
        data: {
          chrome: pageChrome({
            module: FIXED_ASSETS,
            activeMenubarId: 'fa-assets',
            title: 'Editar activo',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              FIXED_ASSETS,
              { id: 'assets', label: 'Activos' },
              { id: 'assets-list', label: 'Activos' },
              { id: 'edit', label: 'Editar activo' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/fixed-assets/assets/assets/asset-edit/general/general.component'
              ).then(m => m.FixedAssetGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/fixed-assets/assets/assets/asset-edit/files/files.component'
              ).then(m => m.FixedAssetFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/fixed-assets/assets/assets/asset-edit/action-log/action-log.component'
              ).then(m => m.FixedAssetActionLogComponent),
          },
        ],
      },
      {
        path: 'fixed-assets/assets/assets',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/fixed-assets/assets/assets/assets.component').then(
            m => m.FixedAssetsListComponent,
          ),
        data: {
          chrome: pageChrome({
            module: FIXED_ASSETS,
            activeMenubarId: 'fa-assets',
            title: 'Activos',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              FIXED_ASSETS,
              { id: 'assets', label: 'Activos' },
              { id: 'assets-list', label: 'Activos' },
            ),
          }),
        },
      },
      {
        path: 'fixed-assets/reports/assets',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/fixed-assets/reports/assets/assets.component').then(
            m => m.FixedAssetReportAssetsComponent,
          ),
        data: {
          chrome: pageChrome({
            module: FIXED_ASSETS,
            activeMenubarId: 'fa-rep-assets',
            title: 'Activo fijo',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              FIXED_ASSETS,
              { id: 'reports', label: 'Reportes' },
              { id: 'assets', label: 'Activo fijo' },
            ),
          }),
        },
      },
      {
        path: 'fixed-assets/reports/depreciation',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/fixed-assets/reports/depreciation/depreciation.component'
          ).then(m => m.FixedAssetReportDepreciationComponent),
        data: {
          chrome: pageChrome({
            module: FIXED_ASSETS,
            activeMenubarId: 'fa-rep-depreciation',
            title: 'Depreciación',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              FIXED_ASSETS,
              { id: 'reports', label: 'Reportes' },
              { id: 'depreciation', label: 'Depreciación' },
            ),
          }),
        },
      },

      // ── Contabilidad ────────────────────────────────────────────────────
      {
        path: 'accounting/companies/companies',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/accounting/companies/companies/companies.component').then(
            m => m.CompaniesComponent,
          ),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-companies',
            title: 'Empresas',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'companies', label: 'Empresas' },
              { id: 'companies-list', label: 'Empresas' },
            ),
          }),
        },
      },
      {
        path: 'accounting/policies/policies',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/accounting/policies/policies/policies.component').then(
            m => m.PoliciesComponent,
          ),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-policies',
            title: 'Pólizas',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'policies', label: 'Pólizas' },
              { id: 'policies-list', label: 'Pólizas' },
            ),
          }),
        },
      },
      {
        path: 'accounting/policies/policies/nuevo',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/policies/policies/policy-form/policy-form.component'
          ).then(m => m.PolicyFormComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-policies',
            title: 'Nueva póliza',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'policies', label: 'Pólizas' },
              { id: 'policies-list', label: 'Pólizas' },
              { id: 'new', label: 'Nueva póliza' },
            ),
          }),
        },
      },
      {
        path: 'accounting/policies/policies/editar',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/policies/policies/policy-form/policy-form.component'
          ).then(m => m.PolicyFormComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-policies',
            title: 'Editar póliza',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'policies', label: 'Pólizas' },
              { id: 'policies-list', label: 'Pólizas' },
              { id: 'edit', label: 'Editar póliza' },
            ),
          }),
        },
      },
      {
        path: 'accounting/policies/movement-transfers',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/policies/movement-transfers/movement-transfers.component'
          ).then(m => m.MovementTransfersComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-pol-transfers',
            title: 'Traspasos de movimientos',
            titleBarMode: 'report',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'policies', label: 'Pólizas' },
              { id: 'movement-transfers', label: 'Traspasos de movimientos' },
            ),
          }),
        },
      },
      {
        path: 'accounting/policies/download-policies',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/policies/download-policies/download-policies.component'
          ).then(m => m.DownloadPoliciesComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-pol-download',
            title: 'Bajar pólizas',
            titleBarMode: 'report',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'policies', label: 'Pólizas' },
              { id: 'download-policies', label: 'Bajar pólizas' },
            ),
          }),
        },
      },
      {
        path: 'accounting/policies/upload-policies',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/policies/upload-policies/upload-policies.component'
          ).then(m => m.UploadPoliciesComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-pol-upload',
            title: 'Cargar pólizas',
            titleBarMode: 'data-table',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'policies', label: 'Pólizas' },
              { id: 'upload-policies', label: 'Cargar pólizas' },
            ),
          }),
        },
      },
      {
        path: 'accounting/policies/upload-policies/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/policies/upload-policies/upload-policy-form/upload-policy-form.component'
          ).then(m => m.UploadPolicyFormComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-pol-upload',
            title: 'Nueva carga de pólizas',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'policies', label: 'Pólizas' },
              { id: 'upload-policies', label: 'Cargar pólizas' },
              { id: 'new', label: 'Nueva carga de pólizas' },
            ),
          }),
        },
      },
      {
        path: 'accounting/policies/upload-policies/:uploadId/edit',
        loadComponent: () =>
          import(
            './pages/accounting/policies/upload-policies/upload-policy-edit/upload-policy-edit.component'
          ).then(m => m.UploadPolicyEditComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-pol-upload',
            title: 'Editar carga de pólizas',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'policies', label: 'Pólizas' },
              { id: 'upload-policies', label: 'Cargar pólizas' },
              { id: 'edit', label: 'Editar carga de pólizas' },
            ),
          }),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/accounting/policies/upload-policies/upload-policy-edit/upload-policy-edit.component'
              ).then(m => m.UploadPolicyEditTabComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/accounting/policies/upload-policies/upload-policy-edit/upload-policy-edit.component'
              ).then(m => m.UploadPolicyEditTabComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/accounting/policies/upload-policies/upload-policy-edit/upload-policy-edit.component'
              ).then(m => m.UploadPolicyEditTabComponent),
          },
        ],
      },
      {
        path: 'accounting/policies/erp-documents',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/policies/erp-documents/erp-documents.component'
          ).then(m => m.ErpDocumentsComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-pol-erp-docs',
            title: 'Documentos ERP',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'policies', label: 'Pólizas' },
              { id: 'erp-documents', label: 'Documentos ERP' },
            ),
          }),
        },
      },
      {
        path: 'accounting/configuration/periods',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/configuration/periods/periods.component'
          ).then(m => m.AccountingPeriodsComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-cfg-periods',
            title: 'Periodos',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'configuration', label: 'Configuración' },
              { id: 'periods', label: 'Periodos' },
            ),
          }),
        },
      },
      {
        path: 'accounting/configuration/erp-document-templates',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/configuration/erp-document-templates/erp-document-templates.component'
          ).then(m => m.ErpDocumentTemplatesComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-cfg-templates',
            title: 'Plantillas documentos ERP',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'configuration', label: 'Configuración' },
              { id: 'erp-document-templates', label: 'Plantillas documentos ERP' },
            ),
          }),
        },
      },
      {
        path: 'accounting/accounting-accounts/accounting-accounts',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/accounting-accounts/accounting-accounts/accounting-accounts.component'
          ).then(m => m.AccountingAccountsComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-cat-chart',
            title: 'Cuentas contables',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'acc-catalogs', label: 'Cuentas contables' },
              { id: 'accounting-accounts', label: 'Cuentas contables' },
            ),
          }),
        },
      },
      {
        path: 'accounting/accounting-accounts/structure',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/accounting-accounts/structure/structure.component'
          ).then(m => m.AccountingAccountStructureComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-cat-structure',
            title: 'Estructura',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'acc-catalogs', label: 'Cuentas contables' },
              { id: 'structure', label: 'Estructura' },
            ),
          }),
        },
      },
      {
        path: 'accounting/cfdi-download/dashboard',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/cfdi-download/dashboard/cfdi-download-dashboard.component'
          ).then(m => m.CfdiDownloadDashboardComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-cfdi-dashboard',
            title: 'Panel de control',
            titleBarMode: 'report',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'cfdi-download', label: 'Descarga CFDI' },
              { id: 'dashboard', label: 'Panel de control' },
            ),
          }),
        },
      },
      {
        path: 'accounting/cfdi-download/issued',
        loadComponent: () =>
          import(
            './pages/accounting/cfdi-download/issued/cfdi-issued-shell.component'
          ).then(m => m.CfdiIssuedShellComponent),
        children: [
          {
            path: 'income',
            loadComponent: () =>
              import(
                './pages/accounting/cfdi-download/issued/cfdi-issued.component'
              ).then(m => m.CfdiIssuedComponent),
            data: {
              chrome: pageChrome({
                module: ACCOUNTING,
                activeMenubarId: 'acc-cfdi-issued',
                title: 'Emitidos - Ingresos',
                titleBarMode: 'data-table',
                crumbs: moduleCrumbs(
                  ACCOUNTING,
                  { id: 'cfdi-download', label: 'Descarga CFDI' },
                  { id: 'issued', label: 'Emitidos' },
                  { id: 'income', label: 'Ingresos' },
                ),
              }),
            },
          },
          {
            path: 'expenses',
            loadComponent: () =>
              import(
                './pages/accounting/cfdi-download/issued/cfdi-issued.component'
              ).then(m => m.CfdiIssuedComponent),
            data: {
              chrome: pageChrome({
                module: ACCOUNTING,
                activeMenubarId: 'acc-cfdi-issued',
                title: 'Emitidos - Egresos',
                titleBarMode: 'data-table',
                crumbs: moduleCrumbs(
                  ACCOUNTING,
                  { id: 'cfdi-download', label: 'Descarga CFDI' },
                  { id: 'issued', label: 'Emitidos' },
                  { id: 'expenses', label: 'Egresos' },
                ),
              }),
            },
          },
          {
            path: 'transfer',
            loadComponent: () =>
              import(
                './pages/accounting/cfdi-download/issued/transfer/cfdi-issued-transfer.component'
              ).then(m => m.CfdiIssuedTransferComponent),
            data: {
              chrome: pageChrome({
                module: ACCOUNTING,
                activeMenubarId: 'acc-cfdi-issued',
                title: 'Emitidos - Traslado',
                titleBarMode: 'data-table',
                crumbs: moduleCrumbs(
                  ACCOUNTING,
                  { id: 'cfdi-download', label: 'Descarga CFDI' },
                  { id: 'issued', label: 'Emitidos' },
                  { id: 'transfer', label: 'Traslado' },
                ),
              }),
            },
          },
          {
            path: 'payroll',
            loadComponent: () =>
              import(
                './pages/accounting/cfdi-download/issued/payroll/cfdi-issued-payroll.component'
              ).then(m => m.CfdiIssuedPayrollComponent),
            data: {
              chrome: pageChrome({
                module: ACCOUNTING,
                activeMenubarId: 'acc-cfdi-issued',
                title: 'Emitidos - Nómina',
                titleBarMode: 'data-table',
                crumbs: moduleCrumbs(
                  ACCOUNTING,
                  { id: 'cfdi-download', label: 'Descarga CFDI' },
                  { id: 'issued', label: 'Emitidos' },
                  { id: 'payroll', label: 'Nómina' },
                ),
              }),
            },
          },
          {
            path: 'payment',
            loadComponent: () =>
              import(
                './pages/accounting/cfdi-download/issued/payment/cfdi-issued-payment.component'
              ).then(m => m.CfdiIssuedPaymentComponent),
            data: {
              chrome: pageChrome({
                module: ACCOUNTING,
                activeMenubarId: 'acc-cfdi-issued',
                title: 'Emitidos - Pago',
                titleBarMode: 'data-table',
                crumbs: moduleCrumbs(
                  ACCOUNTING,
                  { id: 'cfdi-download', label: 'Descarga CFDI' },
                  { id: 'issued', label: 'Emitidos' },
                  { id: 'payment', label: 'Pago' },
                ),
              }),
            },
          },
        ],
      },
      {
        path: 'accounting/cfdi-download/received',
        loadComponent: () =>
          import(
            './pages/accounting/cfdi-download/issued/cfdi-issued-shell.component'
          ).then(m => m.CfdiIssuedShellComponent),
        children: [
          {
            path: 'income',
            loadComponent: () =>
              import(
                './pages/accounting/cfdi-download/issued/cfdi-issued.component'
              ).then(m => m.CfdiIssuedComponent),
            data: {
              chrome: pageChrome({
                module: ACCOUNTING,
                activeMenubarId: 'acc-cfdi-received',
                title: 'Recibidos - Ingresos',
                titleBarMode: 'data-table',
                crumbs: moduleCrumbs(
                  ACCOUNTING,
                  { id: 'cfdi-download', label: 'Descarga CFDI' },
                  { id: 'received', label: 'Recibidos' },
                  { id: 'income', label: 'Ingresos' },
                ),
              }),
            },
          },
          {
            path: 'expenses',
            loadComponent: () =>
              import(
                './pages/accounting/cfdi-download/issued/cfdi-issued.component'
              ).then(m => m.CfdiIssuedComponent),
            data: {
              chrome: pageChrome({
                module: ACCOUNTING,
                activeMenubarId: 'acc-cfdi-received',
                title: 'Recibidos - Egresos',
                titleBarMode: 'data-table',
                crumbs: moduleCrumbs(
                  ACCOUNTING,
                  { id: 'cfdi-download', label: 'Descarga CFDI' },
                  { id: 'received', label: 'Recibidos' },
                  { id: 'expenses', label: 'Egresos' },
                ),
              }),
            },
          },
          {
            path: 'transfer',
            loadComponent: () =>
              import(
                './pages/accounting/cfdi-download/issued/transfer/cfdi-issued-transfer.component'
              ).then(m => m.CfdiIssuedTransferComponent),
            data: {
              chrome: pageChrome({
                module: ACCOUNTING,
                activeMenubarId: 'acc-cfdi-received',
                title: 'Recibidos - Traslado',
                titleBarMode: 'data-table',
                crumbs: moduleCrumbs(
                  ACCOUNTING,
                  { id: 'cfdi-download', label: 'Descarga CFDI' },
                  { id: 'received', label: 'Recibidos' },
                  { id: 'transfer', label: 'Traslado' },
                ),
              }),
            },
          },
          {
            path: 'payroll',
            loadComponent: () =>
              import(
                './pages/accounting/cfdi-download/issued/payroll/cfdi-issued-payroll.component'
              ).then(m => m.CfdiIssuedPayrollComponent),
            data: {
              chrome: pageChrome({
                module: ACCOUNTING,
                activeMenubarId: 'acc-cfdi-received',
                title: 'Recibidos - Nómina',
                titleBarMode: 'data-table',
                crumbs: moduleCrumbs(
                  ACCOUNTING,
                  { id: 'cfdi-download', label: 'Descarga CFDI' },
                  { id: 'received', label: 'Recibidos' },
                  { id: 'payroll', label: 'Nómina' },
                ),
              }),
            },
          },
          {
            path: 'payment',
            loadComponent: () =>
              import(
                './pages/accounting/cfdi-download/issued/payment/cfdi-issued-payment.component'
              ).then(m => m.CfdiIssuedPaymentComponent),
            data: {
              chrome: pageChrome({
                module: ACCOUNTING,
                activeMenubarId: 'acc-cfdi-received',
                title: 'Recibidos - Pago',
                titleBarMode: 'data-table',
                crumbs: moduleCrumbs(
                  ACCOUNTING,
                  { id: 'cfdi-download', label: 'Descarga CFDI' },
                  { id: 'received', label: 'Recibidos' },
                  { id: 'payment', label: 'Pago' },
                ),
              }),
            },
          },
        ],
      },
      {
        path: 'accounting/tax-compliance/tax-dashboard',
        loadComponent: () =>
          import(
            './pages/accounting/tax-compliance/tax-dashboard/tax-dashboard.component'
          ).then(m => m.TaxDashboardComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-tax-dashboard',
            title: 'Tablero fiscal',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'tax-compliance', label: 'Impuestos y cumplimiento' },
              { id: 'tax-dashboard', label: 'Tablero fiscal' },
            ),
          }),
        },
        children: [
          {
            path: 'summary',
            loadComponent: () =>
              import(
                './pages/accounting/tax-compliance/tax-dashboard/summary/summary.component'
              ).then(m => m.TaxDashboardSummaryComponent),
          },
          {
            path: 'taxes',
            loadComponent: () =>
              import(
                './pages/accounting/tax-compliance/tax-dashboard/taxes/taxes.component'
              ).then(m => m.TaxDashboardTaxesComponent),
          },
          {
            path: 'fiscal-mailbox',
            loadComponent: () =>
              import(
                './pages/accounting/tax-compliance/tax-dashboard/fiscal-mailbox/fiscal-mailbox.component'
              ).then(m => m.TaxDashboardFiscalMailboxComponent),
          },
          {
            path: 'compliance',
            loadComponent: () =>
              import(
                './pages/accounting/tax-compliance/tax-dashboard/compliance/compliance.component'
              ).then(m => m.TaxDashboardComplianceComponent),
          },
        ],
      },
      {
        path: 'accounting/tax-compliance/declarations',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/tax-compliance/declarations/declarations.component'
          ).then(m => m.TaxDeclarationsComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-tax-declarations',
            title: 'Declaraciones',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'tax-compliance', label: 'Impuestos y cumplimiento' },
              { id: 'declarations', label: 'Declaraciones' },
            ),
          }),
        },
      },
      {
        path: 'accounting/tax-compliance/electronic-accounting',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/tax-compliance/electronic-accounting/electronic-accounting.component'
          ).then(m => m.ElectronicAccountingComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-tax-eaccounting',
            title: 'Contabilidad electrónica',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'tax-compliance', label: 'Impuestos y cumplimiento' },
              { id: 'electronic-accounting', label: 'Contabilidad electrónica' },
            ),
          }),
        },
      },
      {
        path: 'accounting/tax-compliance/diot',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/tax-compliance/diot/diot.component'
          ).then(m => m.DiotComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-tax-diot',
            title: 'DIOT',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'tax-compliance', label: 'Impuestos y cumplimiento' },
              { id: 'diot', label: 'DIOT' },
            ),
          }),
        },
      },
      {
        path: 'accounting/tax-compliance/efos',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/tax-compliance/efos/efos.component'
          ).then(m => m.EfosComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-tax-efos',
            title: 'EFOS',
            titleBarMode: 'form',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'tax-compliance', label: 'Impuestos y cumplimiento' },
              { id: 'efos', label: 'EFOS' },
            ),
          }),
        },
      },
      {
        path: 'accounting/reports/financial-position',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/reports/financial-position/financial-position.component'
          ).then(m => m.FinancialPositionComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-rep-financial-position',
            title: 'Estado de situación financiera',
            titleBarMode: 'report',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'reports', label: 'Reportes' },
              { id: 'financial-position', label: 'Estado de situación financiera' },
            ),
          }),
        },
      },
      {
        path: 'accounting/reports/income-statement',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/reports/income-statement/income-statement.component'
          ).then(m => m.IncomeStatementComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-rep-income',
            title: 'Estado de resultados',
            titleBarMode: 'report',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'reports', label: 'Reportes' },
              { id: 'income-statement', label: 'Estado de resultados' },
            ),
          }),
        },
      },
      {
        path: 'accounting/reports/cash-flow',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/reports/cash-flow/cash-flow.component'
          ).then(m => m.CashFlowComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-rep-cash-flow',
            title: 'Estado de flujos de efectivo',
            titleBarMode: 'report',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'reports', label: 'Reportes' },
              { id: 'cash-flow', label: 'Estado de flujos de efectivo' },
            ),
          }),
        },
      },
      {
        path: 'accounting/reports/equity-changes',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/reports/equity-changes/equity-changes.component'
          ).then(m => m.EquityChangesComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-rep-equity-changes',
            title: 'Estado de cambios en el capital contable',
            titleBarMode: 'report',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'reports', label: 'Reportes' },
              {
                id: 'equity-changes',
                label: 'Estado de cambios en el capital contable',
              },
            ),
          }),
        },
      },
      {
        path: 'accounting/reports/trial-balance',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/reports/trial-balance/trial-balance.component'
          ).then(m => m.TrialBalanceComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-rep-trial-balance',
            title: 'Balanza de comprobación',
            titleBarMode: 'report',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'reports', label: 'Reportes' },
              { id: 'trial-balance', label: 'Balanza de comprobación' },
            ),
          }),
        },
      },
      {
        path: 'accounting/reports/auxiliary-movements',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/reports/auxiliary-movements/auxiliary-movements.component'
          ).then(m => m.AuxiliaryMovementsComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-rep-auxiliary-movements',
            title: 'Movimientos auxiliares',
            titleBarMode: 'report',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'reports', label: 'Reportes' },
              { id: 'auxiliary-movements', label: 'Movimientos auxiliares' },
            ),
          }),
        },
      },
      {
        path: 'accounting/reports/analytics',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/reports/analytics/analytics.component'
          ).then(m => m.AnalyticsComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-rep-analytics',
            title: 'Analiticas',
            titleBarMode: 'report',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'reports', label: 'Reportes' },
              { id: 'analytics', label: 'Analiticas' },
            ),
          }),
        },
      },
      {
        path: 'accounting/reports/policy-journal',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/accounting/reports/policy-journal/policy-journal.component'
          ).then(m => m.PolicyJournalComponent),
        data: {
          chrome: pageChrome({
            module: ACCOUNTING,
            activeMenubarId: 'acc-rep-policy-journal',
            title: 'Diario de polizas',
            titleBarMode: 'report',
            crumbs: moduleCrumbs(
              ACCOUNTING,
              { id: 'reports', label: 'Reportes' },
              { id: 'policy-journal', label: 'Diario de polizas' },
            ),
          }),
        },
      },
    ],
  },
];
